import anime from "animejs";
import lottie from "lottie-web";

import { INVIEW_ENTER } from "@scroll/constants";
import { roundRect } from "@utils/canvas";
import { $, $$, rect } from "@utils/dom";
import ResizeOrientation from "@utils/resize";
import Viewport from "@utils/viewport";

const INACTIVE_CLASSNAME = "--inactive";
const CONTAINER_FLUID_SM_MARGIN = 30;

class PbRowStickyContent {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;

    this.bg = $('.pb-row-sticky-content__bg', this.el);
    this.contents = [ ...$$('.pb-row-sticky-content__content', this.el) ].map(content => new Content(content));
    this.medias = [ ...$$('.pb-row-sticky-content__media__inner', this.el) ].map(media => new Media(media));

    this._currentIndex = 0;

    this._onResize = this._onResize.bind(this);
    this._onContent = this._onContent.bind(this);
    this._onMedia = this._onMedia.bind(this);
    this._onModule = this._onModule.bind(this);

    this.init();
  }

  init() {
    if( this.bg ) {
      this.bg = new Background(this.bg);
      if( this.contents && this.contents[0].bgColor ) this.bg.init(this.contents[0].bgColor);
    }

    this._bindEvents();
  }
  destroy() {
    this._unbindEvents();

    if( this.bg ) this.bg.destroy();
    if( this.contents ) this.contents.forEach(content => content.destroy());
    if( this.medias ) this.medias.forEach(media => media.destroy());

    this.el = null;
    this.emitter = null;
    this.bg = null;
    this.contents = null;
    this.medias = null;

    this._currentIndex = null;

    this._onResize = null;
    this._onContent = null;
    this._onMedia = null;
    this._onModule = null;
  }


  _bindEvents() {
    this.emitter.on('SiteScroll.pb-row-sticky-content', this._onModule);
    this.emitter.on('SiteScroll.pb-row-sticky-content__content', this._onContent);
    this.emitter.on('SiteScroll.pb-row-sticky-content__media', this._onMedia);

    if( this.bg ) ResizeOrientation.add(this._onResize);
  }
  _unbindEvents() {
    this.emitter.off('SiteScroll.pb-row-sticky-content', this._onModule);
    this.emitter.off('SiteScroll.pb-row-sticky-content__content', this._onContent);
    this.emitter.off('SiteScroll.pb-row-sticky-content__media', this._onMedia);

    if( this.bg ) ResizeOrientation.remove(this._onResize);
  }

  _onResize() {
    // do nothing for viewport smaller than 1200px
    if( Viewport.width < 1200 ) return;

    this.bg?.resize();
  }
  _onContent(direction, obj) {
    // do nothing for viewport smaller than 1200px
    if( Viewport.width < 1200 ) return;

    // do nothing for "exit"
    if( direction !== INVIEW_ENTER ) return;

    // find index of content who triggered scroll call event
    const index = this.contents.findIndex(content => content.el === obj.el);

    // if content is found and not already selected
    if( index < 0 && index !== this._currentIndex ) return;

    // hide previous elements
    this.contents[this._currentIndex].hide();
    this.medias[this._currentIndex].hide();

    // update current index
    this._currentIndex = index;

    // update background color if necessary
    if( this.bg && this.contents[this._currentIndex].bgColor ) this.bg.color = this.contents[this._currentIndex].bgColor;

    // show current elements
    this.contents[this._currentIndex].show();
    this.medias[this._currentIndex].show();
  }
  _onMedia(direction, obj) {
    // do nothing for viewport larger than 1200px
    if( Viewport.width >= 1200 ) return;

    // find media related to event
    const media = this.medias.find(media => media.el === obj.el);
    if( !media ) return;

    // toggle media playback
    if( direction === INVIEW_ENTER ) media.show();
    else media.hide();
  }
  _onModule(direction, obj) {
    // do nothing for viewport smaller than 1200px
    if( Viewport.width < 1200 || obj.el !== this.el ) return;

    // play/pause media when module enter/exit viewport
    this.medias[this._currentIndex][direction === INVIEW_ENTER ? 'play' : 'pause']();
  }
}


class Media {
  constructor(el) {
    this.el = el;
    this.lottie = $('.pb-row-sticky-content__media__lottie', this.el);

    this._instance = null;
    this._ready = false;
    this._visible = false;

    this._onLottieReady = this._onLottieReady.bind(this);

    this.init();
  }

  init() {
    if( this.lottie ) {
      this._instance = lottie.loadAnimation({
        container: this.lottie,
        path: this.lottie.getAttribute('data-animation-path'),
        autoplay: false,
        loop: false,
        renderer: 'svg',
      });

      this._instance.addEventListener("DOMLoaded", this._onLottieReady);
    }
  }
  destroy() {
    if( this._instance ) this._instance.destroy();

    this.el = null;
    this.lottie = null;

    this._instance = null;
    this._ready = null;
    this._visible = null;

    this._onLottieReady = null;
  }

  hide() {
    if( !this._visible ) return;
    this._visible = false;

    this.el.classList.add(INACTIVE_CLASSNAME);
    if( this._instance ) this._instance.pause();
  }
  show() {
    if( this._visible ) return;
    this._visible = true;

    if( this._instance ) {
      //if( Viewport.width < 1200 ) this._instance.play();
      //else this._instance.goToAndPlay(0);

      this._instance.goToAndPlay(0);
    }

    this.el.classList.remove(INACTIVE_CLASSNAME);
  }

  play() {
    if( this._instance ) this._instance.play();
  }
  pause() {
    if( this._instance ) this._instance.pause();
  }

  _onLottieReady() {
    this._ready = true;
    if( this._visible && this._instance ) this._instance.play();
  }
}

class Content {
  constructor(el) {
    this.el = el;

    const style = getComputedStyle(this.el);

    this.bgColor = style.getPropertyValue('--content-bg-color').trim();
    this.textColor = style.getPropertyValue('--content-text-color').trim();

    if( this.bgColor === 'transparent' ) this.bgColor = 'rgba(255,255,255,0)';
    if( this.textColor === 'currentColor' ) this.textColor = null;
  }

  destroy() {
    this.el = null;
    this.bgColor = null;
    this.textColor = null;
  }
  show() {
    this.el.classList.remove(INACTIVE_CLASSNAME);
  }
  hide() {
    this.el.classList.add(INACTIVE_CLASSNAME);
  }
}

class Background {
  constructor(el) {
    this.el = el;

    this._bcr = null;
    this._ctx = this.el.getContext('2d');
    this._color = null;
    this._dummy = { color: null };
    this._init = false;

    this._onUpdate = this._onUpdate.bind(this);

    this._resize();
  }

  init(color) {
    this._init = true;
    this._color = color;
    this._dummy.color = color;

    this._draw(this._color);
  }
  destroy() {
    this.el = null;

    this._bcr = null;
    this._color = null;
    this._ctx = null;
    this._dummy = null;
    this._init = null;

    this._onUpdate = null;
  }
  resize() {
    this._resize();
    this._draw(this._dummy.color);
  }
  
  _resize() {
    this._bcr = rect(this.el);
    const dpr = Viewport.devicePixelRatio;

    this.el.width = this._bcr.width * dpr;
    this.el.height = this._bcr.height * dpr;

    this._ctx.scale(dpr, dpr);
  }
  _draw(color) {
    const { width, height } = this._bcr;

    this._ctx.clearRect(0, 0, width, height);
    this._ctx.fillStyle = color;

    roundRect(this._ctx, 0, CONTAINER_FLUID_SM_MARGIN, width, height - CONTAINER_FLUID_SM_MARGIN * 2, 10, true, false);
  }
  _onUpdate() {
    this._draw(this._dummy.color);
  }

  // getter - setter
  get color() { return this._color; }
  set color(value) {
    if( this._color === value ) return;
    this._color = value;

    anime({
      targets: this._dummy,
      color: value,
      duration: 250,
      easing: 'linear',
      update: this._onUpdate,
    });
  }
}

export default PbRowStickyContent;
